import { useState } from "react";
import { toast } from "react-toastify";
import { useAdminOrganizationControllerUpdateStatus } from "service";
import { AdminOrganizationResDto } from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "@/query-client";

export interface UseOrganizationAccessToggle {
  pendingOrganization: AdminOrganizationResDto | null;
  isSubmitting: boolean;
  requestToggle: (organization: AdminOrganizationResDto) => void;
  confirmToggle: () => void;
  cancelToggle: () => void;
}

const useOrganizationAccessToggle = (): UseOrganizationAccessToggle => {
  const [pendingOrganization, setPendingOrganization] =
    useState<AdminOrganizationResDto | null>(null);

  const { mutate, isLoading } = useAdminOrganizationControllerUpdateStatus({
    onSuccess: (response) => {
      toast.success(
        response.data?.isActive
          ? "Access Successfully Restored."
          : "Access Successfully Removed.",
      );
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/organizations",
          operationId: "adminOrganizationControllerFindMany",
          variables: {},
        }),
      );
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/accounts",
          operationId: "adminAccountControllerGetAll",
          variables: {},
        }),
      );
      if (pendingOrganization) {
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/organizations/{id}",
            operationId: "adminOrganizationControllerFindById",
            variables: { pathParams: { id: pendingOrganization.id } },
          }),
        );
      }
      setPendingOrganization(null);
    },
    onError: () => {
      toast.error("Couldn't update organization access. Please try again.");
    },
  });

  const requestToggle = (organization: AdminOrganizationResDto) => {
    setPendingOrganization(organization);
  };

  const cancelToggle = () => setPendingOrganization(null);

  const confirmToggle = () => {
    if (!pendingOrganization) return;
    mutate({
      pathParams: { id: pendingOrganization.id },
      body: { isActive: !pendingOrganization.isActive },
    });
  };

  return {
    pendingOrganization,
    isSubmitting: isLoading,
    requestToggle,
    confirmToggle,
    cancelToggle,
  };
};

export default useOrganizationAccessToggle;
